import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { useAuthContext } from "../hooks/useAuthContext";
import { storePromo } from "../lib/billingApi";

// Public partner landing pages. Each one stashes its code so it survives signup → verify →
// login; AuthedRedirect then sends the user to /pricing?claim=1, which opens the checkout
// with the code applied. Prices are display-only — the server validates the code and the
// Razorpay Offer applies the actual discount.
const PARTNERS = {
    "startup-grants": {
        code: "SGI20",
        partnerName: "Startup Grants India",
        headline: "20% off Content Flash AI Starter for Startup Grants India founders",
        fullPrice: "₹15,990",
        offerPrice: "₹12,792",
        terms: "Starter plan, annual billing in INR. New accounts only. Valid until 31 Jan 2027. Renews at the regular price.",
    },
};

const PERKS = [
    "AI-generated forms and conversational mode",
    "Response Intelligence on every submission",
    "Conditional logic and remove ContentFlashAI branding",
];

export default function PartnerOffer({ partner }) {
    const offer = PARTNERS[partner];
    const { user } = useAuthContext();
    const navigate = useNavigate();

    useEffect(() => { if (offer) storePromo(offer.code); }, [offer]);

    if (!offer) return null;

    const claim = () => navigate(user ? "/pricing?claim=1" : "/signup");

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50 dark:bg-[#1a1c2b] font-Rajdhani">
            <Helmet><title>{`${offer.partnerName} offer - Content Flash AI`}</title></Helmet>
            <div className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-[#23263a] p-7 shadow-sm">
                <span className="inline-block text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-indigo-600 text-white">
                    {offer.partnerName} exclusive
                </span>
                <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{offer.headline}</h1>

                <div className="mt-5 flex items-baseline gap-2">
                    <span className="text-lg text-gray-400 line-through">{offer.fullPrice}</span>
                    <span className="text-4xl font-bold font-display text-gray-900 dark:text-white">{offer.offerPrice}</span>
                    <span className="text-sm text-gray-500">/first year</span>
                </div>

                <ul className="mt-5 space-y-2">
                    {PERKS.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <FiCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> {p}
                        </li>
                    ))}
                </ul>

                <div className="mt-6 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    Your code <span className="font-bold text-indigo-700 dark:text-indigo-300">{offer.code}</span> is applied automatically at checkout.
                </div>

                <button onClick={claim} className="mt-6 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition">
                    {user ? "Claim offer" : "Create your account to claim"}
                </button>
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">{offer.terms}</p>
            </div>
        </div>
    );
}
